import api from '@/services/Api';
import {
  clearKeycloakCallbackHash,
  clearKeycloakCallbackStorage,
  getKeycloakRedirectUri,
  keycloak,
  restorePostLoginPath,
  savePostLoginPath,
} from './keycloakRedirect';

export { keycloak };

function isMaintenanceError(error) {
  return error?.response?.status === 503;
}

async function loadAuthenticatedUser(store) {
  try {
    await store.dispatch('auth/setUser');
  } catch (error) {
    if (isMaintenanceError(error)) {
      await store.dispatch('maintenance/enable');
    }
  }
}

async function detectApiMaintenance(store) {
  try {
    await api.get('/status');
    return false;
  } catch (error) {
    if (isMaintenanceError(error)) {
      await store.dispatch('maintenance/enable');
      return true;
    }
    return false;
  }
}

function completeForMaintenance(store, authComplete) {
  store.dispatch('auth/isAuthenticating', false);
  authComplete();
}

export async function KeycloakAuthInit(options) {
  const { store, authComplete } = options;

  store.dispatch('auth/isAuthenticating', true);

  if (await detectApiMaintenance(store)) {
    completeForMaintenance(store, authComplete);
    return;
  }

  keycloak.onAuthSuccess = async () => {
    await store.dispatch('auth/setKeycloakTokens', {
      token: keycloak.token,
      refreshToken: keycloak.refreshToken,
    });

    setInterval(() => keycloak.updateToken(30), 100000);

    try {
      store.dispatch('auth/monitorAuthenticated');
    } catch (e) {
      console.error('Keycloak initial error', e);
    }
  };

  keycloak.onAuthLogout = async () => {
    await store.dispatch('auth/resetUser');
    await store.dispatch('auth/setKeycloakTokens', {});
  };

  keycloak.onAuthRefreshSuccess = async () => {
    await store.dispatch('auth/setKeycloakTokens', {
      token: keycloak.token,
      refreshToken: keycloak.refreshToken,
    });
  };

  try {
    await keycloak.init({
      pkceMethod: 'S256',
      redirectUri: getKeycloakRedirectUri(),
      enableLogging: true,
      checkLoginIframe: false,
      // KC26 id tokens can fail nonce checks in keycloak-js 16; do not use login-required
      // (loginRequired makes clearToken() auto-call login() and loops on any auth error).
      useNonce: false,
    });

    if (keycloak.authenticated) {
      clearKeycloakCallbackHash();
      restorePostLoginPath();
      await store.dispatch('auth/setKeycloakTokens', {
        token: keycloak.token,
        refreshToken: keycloak.refreshToken,
      });
      await loadAuthenticatedUser(store);
    }
  } catch (e) {
    console.error('Keycloak init failed', e);
    clearKeycloakCallbackHash();
    clearKeycloakCallbackStorage();
  }

  store.dispatch('auth/isAuthenticating', false);
  store.dispatch('auth/setAuthenticated', keycloak.authenticated);

  if (store.getters['maintenance/isEnabled']) {
    completeForMaintenance(store, authComplete);
    return;
  }

  if (keycloak.authenticated) {
    store.dispatch('auth/isAuthenticating', false);
    authComplete();
    return;
  }

  // Single redirect to Keycloak — do not mount the app until authenticated
  store.dispatch('auth/isAuthenticating', false);
  savePostLoginPath();
  await store.dispatch('auth/login');
}
